import {
  Component,
  effect,
  ElementRef,
  HostBinding,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  Signal,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';

import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { RippleModule } from 'primeng/ripple';
import { MenuItem } from 'primeng/api';

import { children } from '@core/animation';
import { LayoutService } from '@core/service/layout.service';

export type MenuItemWithCreateLink = MenuItem & { createLink?: string[] };

@Component({
  selector: '[app-menuitem]',
  templateUrl: './menu-item.component.html',
  styleUrl: './menu-item.component.scss',
  animations: [children],
  imports: [CommonModule, RouterModule, RippleModule],
})
export class MenuItemComponent implements OnInit, OnDestroy {
  public item: InputSignal<MenuItemWithCreateLink> = input<MenuItemWithCreateLink>();
  public index: InputSignal<number> = input<number>();
  public root: InputSignal<boolean> = input<boolean>();
  public parentKey: InputSignal<string> = input<string>();

  protected active = false;
  protected key: string = '';
  protected readonly submenu: Signal<ElementRef | undefined> = viewChild<ElementRef>('submenu');

  private readonly menuSourceSubscription: Subscription;
  private readonly menuResetSubscription: Subscription;

  constructor(
    public readonly layoutService: LayoutService,
    public readonly router: Router
  ) {
    this.menuSourceSubscription = this.layoutService.menuSourceObservable.subscribe(value => {
      Promise.resolve(null).then(() => {
        if (value.routeEvent) {
          this.active = value.key === this.key || value.key.startsWith(this.key + '-');
          return;
        }

        if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
          this.active = false;
        }
      });
    });

    this.menuResetSubscription = this.layoutService.resetSourceObservable.subscribe(() => {
      this.active = false;
    });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      if (this.item().routerLink) {
        this.updateActiveStateFromRoute();
      }
    });

    effect(() => {
      if (this.layoutService.isOverlay() && this.layoutService.isSidebarActive()) {
        if (this.item().routerLink) {
          this.updateActiveStateFromRoute();
        }
      }
    });
  }

  @HostBinding('class.active-menuitem')
  get activeClass() {
    return this.active && !this.root();
  }

  @HostBinding('class.layout-root-menuitem')
  get isRoot() {
    return !this.parentKey();
  }

  get submenuAnimation() {
    if (this.root()) {
      return 'expanded';
    }

    return this.active ? 'expanded' : 'collapsed';
  }

  public ngOnInit(): void {
    this.key = this.parentKey() ? this.parentKey() + '-' + this.index() : String(this.index());

    if (this.item()?.routerLink) {
      this.updateActiveStateFromRoute();
    }
  }

  public ngOnDestroy(): void {
    if (this.menuSourceSubscription) {
      this.menuSourceSubscription.unsubscribe();
    }

    if (this.menuResetSubscription) {
      this.menuResetSubscription.unsubscribe();
    }
  }

  protected itemClick(event: Event): void {
    if (this.item().disabled) {
      event.preventDefault();
      return;
    }

    if (this.item().command) {
      this.item().command({ originalEvent: event, item: this.item });
    }

    // toggle active state
    if (this.item().items) {
      this.active = !this.active;
    } else if (this.layoutService.isMobile()) {
      this.layoutService.layoutState.update(val => ({
        ...val,
        staticMenuMobileActive: false,
      }));
    }

    this.layoutService.onMenuStateChange({ key: this.key });
  }

  protected createClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.layoutService.isMobile()) {
      this.layoutService.layoutState.update(val => ({
        ...val,
        staticMenuMobileActive: false,
      }));
    }

    this.router.navigate(this.item().createLink);
  }

  private updateActiveStateFromRoute(): void {
    if (this.item()?.routerLink) {
      const activeRoute = this.router.isActive(this.item().routerLink[0], {
        paths: 'exact',
        queryParams: 'ignored',
        matrixParams: 'ignored',
        fragment: 'ignored',
      });

      if (activeRoute) {
        this.layoutService.onMenuStateChange({
          key: this.key,
          routeEvent: true,
        });
      }
    }
  }
}
